import dayjs from 'dayjs';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  CompsetPositioningLevelValues,
  DynamicRateDiscountRangeSettings,
  PriceRangeSettings,
} from 'lumen/models/recommendation-strategy';

// Rolls back unsaved changes on a strategy record only when the record is
// still a live EmberData proxy. Safe to call from willDestroy, where the
// record may already be unloaded or replaced by a non-record default
// (the recommendations controller returns `{} as RecommendationStrategy`
// when no record is in the store — see COA-3642).
export function safeRollback(
  strategy: RecommendationStrategy | null | undefined,
): void {
  if (
    strategy &&
    !strategy.isDestroyed &&
    !strategy.isDestroying &&
    typeof strategy.rollbackAttributes === 'function'
  ) {
    strategy.rollbackAttributes();
  }
}

type DayWithPriceRangeError = {
  dow: number;
  isConditional: boolean;
};

type MultiRoomDayWithPriceRangeError = {
  dow: number;
  roomId: string;
};

const isMinMaxValid = (
  minimum: number | null | undefined,
  maximum: number | null | undefined,
): boolean => {
  return minimum == null || maximum == null || minimum <= maximum;
};

const isMissingDiscountBound = (
  rate: DynamicRateDiscountRangeSettings['rate_discounts'][string],
): boolean => {
  return rate.minimum?.value == null || rate.maximum?.value == null;
};

// A lone min or max can't map to a lead time band (conditional strategy) discount range, so a band must set both
// bounds or clear both (clearing falls back to the default strategy's range).
const isPartialDiscountBound = (
  rate: DynamicRateDiscountRangeSettings['rate_discounts'][string],
): boolean => {
  return (rate.minimum?.value == null) !== (rate.maximum?.value == null);
};

export function getDaysWithPriceRangeError(
  settings: PriceRangeSettings,
  isConditional: boolean,
): DayWithPriceRangeError[] {
  const configuration = Object.entries(settings.day_of_week ?? {});
  const daysWithPriceRangeError: DayWithPriceRangeError[] = [];

  for (const [dayOfWeek, { maximum, minimum }] of configuration) {
    const isValid = isMinMaxValid(minimum, maximum);

    if (!isValid) {
      daysWithPriceRangeError.push({
        dow: parseInt(dayOfWeek, 10),
        isConditional,
      });
    }
  }

  return daysWithPriceRangeError;
}

export function getMultiRoomDaysWithPriceRangeError(
  settings: PriceRangeSettings,
): MultiRoomDayWithPriceRangeError[] {
  const configuration = Object.entries(settings.overrides ?? {});
  const multiRoomDaysWithPriceRangeError: MultiRoomDayWithPriceRangeError[] =
    [];

  for (const [dayOfWeek, roomSettings] of configuration) {
    for (const [roomId, { maximum, minimum }] of Object.entries(roomSettings)) {
      const isValid = isMinMaxValid(minimum, maximum);

      if (!isValid) {
        multiRoomDaysWithPriceRangeError.push({
          dow: parseInt(dayOfWeek, 10),
          roomId: roomId,
        });
      }
    }
  }

  return multiRoomDaysWithPriceRangeError;
}

export type DynamicRateDiscountRangeErrorKind =
  | 'minGreaterThanMax'
  | 'minOrMaxMissing'
  | 'minOrMaxPartiallySet';

export const DYNAMIC_RATE_DISCOUNT_RANGE_ERROR_KIND_PRECEDENCE: DynamicRateDiscountRangeErrorKind[] =
  ['minOrMaxMissing', 'minOrMaxPartiallySet', 'minGreaterThanMax'];

// Each rate matches at most one kind: `minOrMaxMissing` (parent row, a bound
// missing), `minOrMaxPartiallySet` (leadtime band, exactly one bound set), or
// `minGreaterThanMax` (both bounds present, min > max). A strategy is either a
// parent or a band, so `minOrMaxMissing` and `minOrMaxPartiallySet` never both
// appear; with several rates a strategy can still mix one of those with
// `minGreaterThanMax` - see DYNAMIC_RATE_DISCOUNT_RANGE_ERROR_KIND_PRECEDENCE.
export function getRatesWithDiscountRangeError(
  settings: DynamicRateDiscountRangeSettings,
  requireBothValues: boolean,
): Record<DynamicRateDiscountRangeErrorKind, string[]> {
  const ratesWithError: Record<DynamicRateDiscountRangeErrorKind, string[]> = {
    minGreaterThanMax: [],
    minOrMaxMissing: [],
    minOrMaxPartiallySet: [],
  };

  for (const [rate, discount] of Object.entries(
    settings.rate_discounts ?? {},
  )) {
    const { maximum, minimum } = discount;

    if (requireBothValues && isMissingDiscountBound(discount)) {
      ratesWithError.minOrMaxMissing.push(rate);
    } else if (!requireBothValues && isPartialDiscountBound(discount)) {
      ratesWithError.minOrMaxPartiallySet.push(rate);
    } else if (!isMinMaxValid(minimum?.value, maximum?.value)) {
      ratesWithError.minGreaterThanMax.push(rate);
    }
  }

  return ratesWithError;
}

export function updateCompetitorImpactForSpecifiedDays(
  strategy: RecommendationStrategy,
  competitors: string[],
  newLevel: CompsetPositioningLevelValues,
  daysToUpdate: number[],
): void {
  const { settings } = strategy;

  if (
    settings.settings_type !== 'compset_positioning' ||
    !settings.day_of_week
  ) {
    return;
  }

  let newSettings = { ...settings };

  daysToUpdate.forEach((dayOfWeek) => {
    const dowSettings = newSettings.day_of_week[dayOfWeek];

    if (dowSettings) {
      competitors.forEach((competitor) => {
        newSettings = {
          ...newSettings,
          day_of_week: {
            ...newSettings.day_of_week,
            [dayOfWeek]: {
              ...newSettings.day_of_week[dayOfWeek],
              [competitor]: newLevel,
            },
          },
        };
      });
    }
  });

  strategy.settings = newSettings;
}

/**
 * Util that sorts seasonal strategies passed in, based on a few specific checks:
 * - strategies that are being created need to come first (records without an id)
 * - if strategies have an id they should be sorted ASC based on the start date (seasonStartDt)
 * - if strategies have the same start date, they'll be sorted by creation date (createdAt) - most recent first
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function sortSeasonalStrategies(strategies: RecommendationStrategy[]) {
  return strategies
    .filter((strategy) => strategy.isSeasonalStrategy)
    .sort((a, b) => {
      // if the records don't have an id yet, they're being created. In this case they should be rendered at the top
      // Note: ideally we could use isNew here, but we ran into some linting issues. Can be investigated later
      if (!a.id) {
        return -1;
      }
      if (!b.id) {
        return 1;
      }

      if (a.seasonStartDt && b.seasonStartDt && a.createdAt && b.createdAt) {
        if (a.seasonStartDt.isSame(b.seasonStartDt, 'day')) {
          return b.createdAt.isAfter(a.createdAt) ? 1 : -1;
        }

        return b.seasonStartDt.isAfter(a.seasonStartDt) ? -1 : 1;
      }
      return -1;
    });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getWeekDayColumns = () => {
  const startOfWeek = dayjs().startOf('week');

  return Array.from({ length: 7 }, (_, dayOfWeek) => {
    const currentDay = startOfWeek.add(dayOfWeek, 'day');
    const weekDayId = currentDay.isoWeekday();
    const isoWeekDay = REVERSE_ISO_DAYS[weekDayId];

    return {
      id: weekDayId,
      isoWeekDay,
    };
  });
};
